import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { themeColours, type ReleaseKitLook } from 'fym-shared/src/music24/releaseThemes';
import {
  AssetStyleId,
  defaultStyleFor,
  ResolvedAssetStyle,
  stylesOfAssetType,
  WaveformShape,
  waveformsOfAssetType,
} from 'fym-shared/src/music24/assetStyles';
import { STUDIO_THEMES, StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import { isLyricSampleStyle } from 'fym-shared/src/music24/releaseKitLookSamples';
import { TypedMemo } from '../../../../common/types';
import { ThemeStill } from '../../themes/ThemeStill';
import { LyricCardSample } from '../../themes/LyricCardSample';
import { ThemeStillLayout } from '../../themes/themeStillVars';
import { LoopStyleId, StyleLoopPreview } from '../../themes/StyleLoopPreview';
import { assetStyleMessages, waveformShapeMessages } from '../../themes/assetStyleMessages';
import { releaseThemeMessages } from '../../themes/releaseThemeMessages';
import { studioMessages } from '../studioMessages';
import { ChoiceChips } from './ChoiceChips';
import { AssetFrame, isFrameAssetType } from '../frames/AssetFrame';
import type { FrameLyric } from '../frames/assetFrameVars';
import { DetailField } from './DetailField';
import { WaveformGlyph } from '../aiReel/WaveformGlyph';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Studio; each part uses its own slice of it */
import styles from '../Studio.module.scss';
/* eslint-enable css-modules/no-unused-class */

const IMAGE_LAYOUTS: Partial<Record<AssetStyleId, ThemeStillLayout>> = {
  coverCard: 'card',
  coverGround: 'ground',
  typeOnly: 'type',
};

/** The asset types whose files are 9:16: their previews are tall. */
const TALL_TYPES: ReadonlySet<StudioThemeId> = new Set(['story', 'countdown', 'canvasLoop', 'artworkInMotion']);

export interface StylePickerSong {
  artworkUrl: string | undefined;
  title: string;
  artist: string;
  kicker: string;
}

interface StylePickerProps {
  assetTypeId: StudioThemeId;
  /** The look of the song: the theme of every preview and of the default style. */
  look: ReleaseKitLook;
  palette: readonly string[];
  song: StylePickerSong;
  value: ResolvedAssetStyle;
  /** AI footage shows only with `canUseAiReels` (owner decision: on every theme). */
  canUseAiReels: boolean;
  /** The sung line of the lyric frames (design s7). */
  lyric?: FrameLyric;
  onChange: (style: AssetStyleId, waveform?: WaveformShape) => void;
}

interface StyleCardProps {
  styleId: AssetStyleId;
  isOn: boolean;
  isThemeDefault: boolean;
  themeName: string;
  preview: React.ReactNode;
  onPick: (style: AssetStyleId) => void;
  onActivate: (style: AssetStyleId | null) => void;
}

const StyleCardComponent: React.FC<StyleCardProps> = ({
  styleId,
  isOn,
  isThemeDefault,
  themeName,
  preview,
  onPick,
  onActivate,
}) => {
  const intl = useIntl();
  const messages = assetStyleMessages[styleId];
  return (
    <button
      type="button"
      role="radio"
      aria-checked={isOn}
      className={clsx(styles.style, isOn && styles.styleOn)}
      data-testid={`style-${styleId}`}
      onClick={() => onPick(styleId)}
      onMouseEnter={() => onActivate(styleId)}
      onMouseLeave={() => onActivate(null)}
      onFocus={() => onActivate(styleId)}
      onBlur={() => onActivate(null)}
    >
      {preview}
      {styleId === 'aiFootage' && (
        <span className={styles.styleAi}>{intl.formatMessage(studioMessages.styleAiBadge)}</span>
      )}
      <b className={styles.styleName}>
        {intl.formatMessage(messages.name)}
        {isThemeDefault && <span className={styles.styleTheme}>{themeName}</span>}
      </b>
      <small className={styles.styleLine}>{intl.formatMessage(messages.line)}</small>
    </button>
  );
};

const StyleCard = TypedMemo(StyleCardComponent);

/**
 * "Style" of step 3 (design q3b, q3c, q4b): only the styles that the asset type offers
 * (`ASSET_TYPE_STYLES`), each previewed in the song's own theme colours, type and cover. The card
 * with the theme name is the default style of the theme; it is selected when the step opens. A
 * style changes only what moves and where the cover goes, never a colour, a font or the texture.
 * The Waveform style adds the shape (bars, line, ring).
 */
const StylePickerComponent: React.FC<StylePickerProps> = ({
  assetTypeId,
  look,
  palette,
  song,
  value,
  canUseAiReels,
  lyric,
  onChange,
}) => {
  const intl = useIntl();
  const [activeId, setActiveId] = useState<AssetStyleId | null>(null);
  const offered = useMemo(
    () => stylesOfAssetType(assetTypeId).filter((style) => style !== 'aiFootage' || canUseAiReels),
    [assetTypeId, canUseAiReels]
  );
  const themeDefault = defaultStyleFor(look.themeId, assetTypeId);
  const themeName = intl.formatMessage(releaseThemeMessages[look.themeId].name);
  const isImage = STUDIO_THEMES[assetTypeId].format === 'image';
  const tall = TALL_TYPES.has(assetTypeId);
  const playingId = activeId ?? value.style;
  const shapes = useMemo(() => waveformsOfAssetType(assetTypeId).filter((shape) => shape !== 'none'), [assetTypeId]);
  const accent = useMemo(() => themeColours(look.themeId, palette).accent, [look.themeId, palette]);
  // Each shape chip draws its shape, live, in the accent of the theme (owner wish 2026-10-08).
  const shapeOptions = useMemo(
    () =>
      shapes.map((shape) => ({
        value: shape,
        label: (
          <span className="d-inline-flex align-items-center gap-2">
            <WaveformGlyph shape={shape} color={accent} />
            {intl.formatMessage(waveformShapeMessages[shape])}
          </span>
        ),
      })),
    [accent, intl, shapes]
  );

  const pick = useCallback(
    (style: AssetStyleId) => onChange(style, style === 'waveform' ? (value.waveform ?? undefined) : undefined),
    [onChange, value.waveform]
  );
  const pickShape = useCallback((shape: WaveformShape) => onChange('waveform', shape), [onChange]);

  if (offered.length === 0) {
    return null;
  }

  const previewOf = (styleId: AssetStyleId): React.ReactNode => {
    // The full videos, the lyric moment and the motion artwork are drawn as frames (design s7).
    if (isFrameAssetType(assetTypeId)) {
      return (
        <AssetFrame
          assetTypeId={assetTypeId}
          assetStyle={styleId}
          waveform={styleId === 'waveform' ? value.waveform : null}
          look={look}
          palette={palette}
          song={song}
          lyric={lyric}
        />
      );
    }
    // The lyric card shows the rendered lyric card sample of the theme (a chorus line of "Forests").
    if (assetTypeId === 'lyricCard' && isLyricSampleStyle(styleId)) {
      return <LyricCardSample themeId={look.themeId} style={styleId} />;
    }
    const layout = IMAGE_LAYOUTS[styleId];
    if (layout !== undefined) {
      return (
        <ThemeStill
          themeId={look.themeId}
          fontOverride={look.fontOverride}
          palette={palette}
          artworkUrl={song.artworkUrl}
          title={song.title}
          artist={song.artist}
          kicker={song.kicker}
          layout={layout}
          tall={tall}
        />
      );
    }
    return (
      <StyleLoopPreview
        style={styleId as LoopStyleId}
        waveform={styleId === 'waveform' ? value.waveform : null}
        themeId={look.themeId}
        fontOverride={look.fontOverride}
        palette={palette}
        artworkUrl={song.artworkUrl}
        playing={styleId === playingId}
      />
    );
  };

  const defaultName = themeDefault === null ? '' : intl.formatMessage(assetStyleMessages[themeDefault.style].name);

  return (
    <DetailField
      title={intl.formatMessage(studioMessages.styleTitle)}
      hint={intl.formatMessage(isImage ? studioMessages.styleHintImage : studioMessages.styleHintLoop, {
        theme: themeName,
      })}
    >
      <div
        className={clsx(
          styles.styles,
          offered.length <= 3 && styles.stylesFew,
          offered.length === 4 && styles.stylesFour
        )}
        role="radiogroup"
        aria-label={intl.formatMessage(studioMessages.styleTitle)}
      >
        {offered.map((styleId) => (
          <StyleCard
            key={styleId}
            styleId={styleId}
            isOn={styleId === value.style}
            isThemeDefault={themeDefault?.style === styleId}
            themeName={themeName}
            preview={previewOf(styleId)}
            onPick={pick}
            onActivate={setActiveId}
          />
        ))}
      </div>
      {themeDefault !== null && (
        <span className={styles.styleStarts}>
          {intl.formatMessage(studioMessages.styleStartsWith, { theme: themeName, style: defaultName })}
        </span>
      )}
      {value.style === 'waveform' && shapes.length > 0 && (
        <div className={styles.styleShape}>
          <span className={styles.fieldTitle}>{intl.formatMessage(studioMessages.styleShape)}</span>
          <ChoiceChips
            label={intl.formatMessage(studioMessages.styleShape)}
            options={shapeOptions}
            value={value.waveform ?? undefined}
            onChange={pickShape}
          />
        </div>
      )}
    </DetailField>
  );
};

export const StylePicker = TypedMemo(StylePickerComponent);
